/**
 * `useHorizontalOverflowShadows` — tracks whether the scroll container has content hidden
 * past its inline-start / inline-end edge, so `PinnedColumnShadows` can show a pinned
 * column's shadow ONLY while something actually scrolls beneath it (a pinned column with
 * nothing hidden under it casts no shadow — MUI pinned-shadow parity).
 *
 * Measures on scroll/resize + revalidate-on-content-change, reading the live scroll geometry
 * (`scrollLeft`/`scrollWidth`/`clientWidth`) that exists only after layout and changes on
 * scroll/resize events, never on render — so it is effect-driven, not render-derived
 * (measuring in render would force a reflow and read stale/zero geometry under SSR).
 *
 * LTR-only, matching the preset (which never renders RTL pinning): `start` maps to the left
 * edge and `end` to the right. An RTL host would need the browser's RTL-`scrollLeft`
 * convention folded in — out of scope while the preset is LTR.
 */
import { type RefObject, useEffect, useState } from 'react'
import { useEvent } from 'react-use-event-hook'

export type HorizontalOverflowShadows = {
  /** Content is hidden past the inline-start (left) edge → show the left pinned shadow. */
  start: boolean
  /** Content is hidden past the inline-end (right) edge → show the right pinned shadow. */
  end: boolean
  /**
   * Live width (px) of the container's vertical scrollbar (`offsetWidth − clientWidth`).
   * The right-pinned columns stick to the SCROLLPORT (inside the scrollbar), but a shadow
   * rail placed on the outer card measures from the card edge (OUTSIDE the scrollbar), so
   * the right rail must add this back to stay glued to the pin edge. `0` with overlay
   * scrollbars (macOS) or no vertical overflow. Assumes the scroll element has no L/R
   * border (true for `TableRoot`), so `offsetWidth − clientWidth` is the scrollbar alone.
   */
  scrollbarWidth: number
}

const NO_OVERFLOW: HorizontalOverflowShadows = { start: false, end: false, scrollbarWidth: 0 }

/**
 * @param scrollRef the scroll container (`TableRoot`) ref — stable across renders.
 * @param revalidateKey re-measures when the CONTENT width could change without a viewport
 *   resize (column sizing/visibility/order, populated↔overlay) — the horizontal gap a
 *   `ResizeObserver` on a fixed-width container misses.
 * @returns which inline edges have hidden content (`{ start, end }`); both `false` when the
 *   content fits (no horizontal overflow) or the container isn't mounted yet.
 */
export const useHorizontalOverflowShadows = (
  scrollRef: RefObject<HTMLElement | null>,
  revalidateKey: unknown,
): HorizontalOverflowShadows => {
  const [shadows, setShadows] = useState<HorizontalOverflowShadows>(NO_OVERFLOW)

  // Stable measurer (never a re-subscribe trigger): reads the live geometry off the ref and
  // updates only when a boolean flips, so scrolling within one shadow state costs one layout
  // read and no render (React bails on the unchanged object identity returned below).
  const measure = useEvent(() => {
    const element = scrollRef.current
    if (element === null) {
      return
    }
    const { scrollLeft, scrollWidth, clientWidth, offsetWidth } = element
    // 1px tolerance absorbs sub-pixel scroll offsets.
    const start = scrollLeft > 1
    const end = scrollWidth - clientWidth - scrollLeft > 1
    // Vertical scrollbar width: `offsetWidth` includes it, `clientWidth` does not (and neither
    // includes a border, which `TableRoot` has none of). The right rail adds this to clear it.
    const scrollbarWidth = offsetWidth - clientWidth
    setShadows((prev) =>
      prev.start === start && prev.end === end && prev.scrollbarWidth === scrollbarWidth
        ? prev
        : { start, end, scrollbarWidth },
    )
  })

  // Subscribe ONCE (scroll + viewport resize). Keyed only on the stable `scrollRef`/`measure`
  // so a data change never tears the listeners down and rebuilds them.
  useEffect(
    function subscribeToScrollAndResize() {
      const element = scrollRef.current
      if (element === null) {
        return
      }

      measure()
      element.addEventListener('scroll', measure, { passive: true })
      const resizeObserver = new ResizeObserver(measure)
      resizeObserver.observe(element)

      return () => {
        element.removeEventListener('scroll', measure)
        resizeObserver.disconnect()
      }
    },
    [scrollRef, measure],
  )

  // Re-measure when the content width could change without a scroll or viewport resize
  // (column sizing/visibility/order, populated↔overlay) — the horizontal gap a
  // `ResizeObserver` on a fixed-width container misses. No re-subscribe: only a re-read.
  useEffect(
    function remeasureOnContentChange() {
      // Effect, not render-derived: `measure()` reads live post-layout scroll
      // geometry (`scrollLeft`/`scrollWidth`) that can't be computed during render
      // (see the hook JSDoc) — not a `no-derived-state` violation.
      measure()
    },
    [measure, revalidateKey],
  )

  return shadows
}
